import type { DisplayFormat } from '@packages/core';
import type { RawDomainSchemaProperty } from '../resolvers/types';

/** Maps domain `formatKey` values to `FieldDef.displayFormat`. Shared by FormLoader and ViewLoader. */
export const FORMAT_KEY_TO_DISPLAY_FORMAT: Record<string, DisplayFormat> = {
  DATE_MDY: 'date',
  DATE: 'date',
  DATETIME: 'datetime',
  DATETIME_TZ: 'datetime-tz',
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
  PERCENT: 'percentage',
  NUMBER: 'number',
  PHONE: 'phone',
  SSN: 'ssn',
  EMAIL: 'text',
  BOOLEAN: 'boolean',
  HTML: 'html',
  FILE: 'file',
  MASKED: 'masked',
  TAX_ID_US: 'ssn',
  MULTILINE: 'multiline',
  MULTILINE_LIST: 'multiline-list',
};

const JSON_SCHEMA_FORMAT_TO_DISPLAY_FORMAT: Record<string, DisplayFormat> = {
  date: 'date',
  'date-time': 'datetime',
};

/** Fallback when only `inputFormatter` is declared (no formatKey / displayFormat). */
const INPUT_FORMATTER_TO_DISPLAY_FORMAT: Record<string, DisplayFormat> = {
  SSN_US: 'ssn',
  PHONE_US: 'phone',
  TAX_ID: 'ssn',
};

export function formatKeyToDisplayFormat(formatKey: string): DisplayFormat | undefined {
  return FORMAT_KEY_TO_DISPLAY_FORMAT[formatKey] ?? (formatKey.toLowerCase() as DisplayFormat);
}

/**
 * Resolves the display format for a domain property using the same precedence as form authoring:
 * explicit displayFormat → formatKey → file type → JSON Schema format → inputFormatter.
 */
export function resolvePropertyDisplayFormat(
  prop: RawDomainSchemaProperty,
): DisplayFormat | undefined {
  if (prop.displayFormat != null) {
    return prop.displayFormat as DisplayFormat;
  }
  if (prop.formatKey != null) {
    return formatKeyToDisplayFormat(prop.formatKey);
  }
  if (prop.type === 'file') return 'file';
  if (prop.format != null) {
    return JSON_SCHEMA_FORMAT_TO_DISPLAY_FORMAT[prop.format];
  }
  if (prop.inputFormatter != null) {
    return INPUT_FORMATTER_TO_DISPLAY_FORMAT[prop.inputFormatter];
  }
  return undefined;
}
